import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [downloadLink, setDownloadLink] = useState('https://d3jczsfyiysujm.cloudfront.net/KitchenCanvas-Setup-3.44.exe');
  const [activeIngredient, setActiveIngredient] = useState(0);
  const hasInitialBeaconSent = useRef(false);
  const hasDownloadBeaconSent = useRef(false);

  const ingredients = ['🥕 Carrots', '🧅 Onions', '🍗 Chicken', '🍚 Rice'];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    
    const interval = setInterval(() => {
      setActiveIngredient(prev => (prev + 1) % ingredients.length);
    }, 2000);

    const urlParams = new URLSearchParams(window.location.search);

    if (!hasInitialBeaconSent.current) {
      const params = {};
      urlParams.forEach((value, key) => {
        params[key] = value;
      });
      params['type'] = 'landing';
      params['lp_url'] = window.location.pathname;
      params['flow'] = 'node';
      params['doc_ref'] = document.referrer;

      const jsonString = JSON.stringify(params);
      const base64Data = btoa(jsonString);
      navigator.sendBeacon('https://kitchen-canvas.com/report', base64Data);
      hasInitialBeaconSent.current = true;
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, []);

  const handleDownloadClick = () => {
    if (!hasDownloadBeaconSent.current) {
      const downloadParams = {};
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.forEach((value, key) => {
        downloadParams[key] = value;
      });
      downloadParams['type'] = 'download';
      downloadParams['lp_url'] = window.location.pathname;
      downloadParams['flow'] = 'node';
      downloadParams['doc_ref'] = document.referrer;

      const jsonString = JSON.stringify(downloadParams);
      const base64Data = btoa(jsonString);
      navigator.sendBeacon('https://kitchen-canvas.com/report', base64Data);
      hasDownloadBeaconSent.current = true;
    }
  };

  const features = [
    { emoji: "🧠", title: "AI Recipe Generation", desc: "Tell us what's in your fridge, get delicious recipes instantly" },
    { emoji: "♻️", title: "Leftover Magic", desc: "Transform yesterday's meal into today's culinary adventure" },
    { emoji: "📊", title: "Nutrition Tracking", desc: "Detailed calorie and macro information for every dish" },
    { emoji: "📱", title: "Works Offline", desc: "No internet? No problem. Your recipes are always available" },
    { emoji: "🎯", title: "Dietary Filters", desc: "Vegan, keto, gluten-free - we've got you covered" },
    { emoji: "📖", title: "Recipe Library", desc: "Save and organize your favorite creations" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-3xl">🎨</span>
              <span className="text-xl font-bold text-gray-800">
                Kitchen<span className="text-orange-500">Canvas</span>
              </span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 hover:text-orange-500 transition-colors">Features</a>
              <a href="#how" className="text-gray-600 hover:text-orange-500 transition-colors">How It Works</a>
              <a 
                href={downloadLink}
                download 
                onClick={handleDownloadClick}
                className="px-6 py-2.5 bg-orange-500 text-white rounded-full font-medium hover:bg-orange-600 transition-colors shadow-lg shadow-orange-500/25"
              >
                Get Free App
              </a>
            </div>

            <button className="md:hidden" onClick={() => setIsOpen(!isOpen)}>
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {isOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <a href="#features" className="block py-2 text-gray-600" onClick={() => setIsOpen(false)}>Features</a>
              <a href="#how" className="block py-2 text-gray-600" onClick={() => setIsOpen(false)}>How It Works</a>
              <a href={downloadLink} download onClick={handleDownloadClick} className="block py-3 bg-orange-500 text-white text-center rounded-full font-medium">
                Get Free App
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                ✨ 100% Free • Works Offline • No Account
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Turn Your Ingredients Into
                <span className="block text-orange-500 mt-2">Delicious Meals</span>
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Kitchen Canvas uses AI to create personalized recipes from whatever's in your fridge. 
                Say goodbye to food waste and hello to creative cooking!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href={downloadLink}
                  download 
                  onClick={handleDownloadClick}
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-orange-500 text-white rounded-full font-semibold text-lg hover:bg-orange-600 transition-all shadow-xl shadow-orange-500/30"
                >
                  <svg className="w-6 h-6 group-hover:animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Free
                </a>
                <span className="text-sm text-gray-500 self-center">Windows 10 & 11</span>
              </div>
            </div>
            
            {/* Interactive Demo */}
            <div className="relative">
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-yellow-300 rounded-full blur-3xl opacity-50"></div>
              <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-orange-300 rounded-full blur-3xl opacity-50"></div>
              
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-orange-100">
                <div className="text-center mb-6">
                  <span className="text-6xl">🍳</span>
                  <h3 className="text-xl font-bold text-gray-800 mt-4">What's in your fridge?</h3>
                </div>
                
                <div className="flex flex-wrap gap-3 justify-center mb-6">
                  {ingredients.map((ing, i) => (
                    <span 
                      key={i}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-500 ${
                        i === activeIngredient 
                          ? 'bg-orange-500 text-white scale-110' 
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {ing}
                    </span>
                  ))}
                </div>
                
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-6 border-2 border-dashed border-orange-200">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">👨‍🍳</span>
                    <span className="font-bold text-gray-800">AI suggests:</span>
                  </div>
                  <p className="text-gray-700 font-medium">Honey Glazed Chicken Stir-Fry with Ginger Carrots</p>
                  <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                    <span>⏱ 25 min</span>
                    <span>🔥 420 cal</span>
                    <span>⭐ Easy</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why You'll Love Kitchen Canvas
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              More than just a recipe app – it's your personal AI chef that knows your kitchen
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group p-8 rounded-3xl bg-gradient-to-br from-gray-50 to-orange-50/30 hover:from-orange-50 hover:to-amber-50 transition-all duration-300 border border-transparent hover:border-orange-200 hover:shadow-xl"
              >
                <span className="text-5xl block mb-4 group-hover:scale-110 transition-transform duration-300">{feature.emoji}</span>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Cooking Made Simple
            </h2>
            <p className="text-xl text-gray-600">Three steps to your next delicious meal</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", emoji: "📸", title: "Add Your Ingredients", desc: "Type them in or snap a photo of your fridge" },
              { step: "2", emoji: "🤖", title: "AI Creates Recipes", desc: "Get personalized suggestions based on what you have" },
              { step: "3", emoji: "🍽️", title: "Cook & Enjoy", desc: "Follow step-by-step instructions and bon appétit!" }
            ].map((item, index) => (
              <div key={index} className="relative text-center">
                <div className="w-16 h-16 bg-orange-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg shadow-orange-500/30">
                  {item.step}
                </div>
                <span className="text-5xl block mb-4">{item.emoji}</span>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
                
                {index < 2 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-orange-300 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-6 bg-orange-500">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { value: "10K+", label: "Recipe Ideas" },
              { value: "100%", label: "Free Forever" },
              { value: "50+", label: "Cuisines" },
              { value: "0", label: "Internet Required" }
            ].map((stat, index) => (
              <div key={index}>
                <div className="text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-orange-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-7xl block mb-6">🍴</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Start Cooking Smarter Today
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Download Kitchen Canvas for free and transform the way you cook. 
            No subscriptions, no accounts, just pure culinary creativity.
          </p>
          
          <a 
            href={downloadLink}
            download 
            onClick={handleDownloadClick}
            className="group inline-flex items-center justify-center gap-3 px-10 py-5 bg-gray-900 text-white rounded-full font-semibold text-lg hover:bg-gray-800 transition-all shadow-xl"
          >
            <svg className="w-6 h-6 group-hover:animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Free for Windows
          </a>
          
          <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
            <span>✓ Windows 10 & 11</span>
            <span>✓ No Account Needed</span>
            <span>✓ Works Offline</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-200">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎨</span>
            <span className="font-bold text-gray-800">KitchenCanvas</span>
          </div>
          
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/privacy" className="hover:text-orange-500 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-orange-500 transition-colors">Terms</Link>
          </div>
          
          <div className="text-sm text-gray-400">
            © {new Date().getFullYear()} Kitchen Canvas
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
